import { useState } from 'react';
import { Link } from 'react-router-dom';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import type { Me } from '@wo/shared';
import { api, errorText } from '../../lib/api';
import { dateTime } from '../../lib/format';
import { Button, Card, ErrorText, Field, Input, Loading, PageHeader, Select, Table, Textarea } from '../../components/ui';

interface Template {
  key: string;
  label: string;
  to: string;
  vars: string[];
  customer: boolean;
  subject: string;
  body: string;
  version: number;
  updatedAt: string | null;
}
interface Version {
  id: number;
  version: number;
  subject: string;
  body: string;
  createdAt: string;
  createdBy: number | null;
}
interface LogRow {
  id: number;
  workOrderId: number | null;
  to: string;
  subject: string;
  template: string;
  status: 'QUEUED' | 'SENT' | 'FAILED';
  attempts: number;
  error: string | null;
  gmailMessageId: string | null;
  sentAt: string | null;
  createdAt: string;
}

/** Email templates (Admin edits; every save is a new version), test send, and the email log. */
export function EmailPage({ me }: { me: Me }) {
  const isAdmin = me.role === 'ADMIN';
  const qc = useQueryClient();
  const templates = useQuery({ queryKey: ['email-templates'], queryFn: () => api.get<Template[]>('/api/email/templates') });
  const [status, setStatus] = useState('');
  const log = useQuery({ queryKey: ['email-log', status], queryFn: () => api.get<LogRow[]>(`/api/email/log${status ? `?status=${status}` : ''}`) });
  const [key, setKey] = useState<string | null>(null);

  if (templates.isLoading) return <Loading />;
  const current = templates.data?.find((t) => t.key === key) ?? null;

  return (
    <>
      <PageHeader title="Email">{isAdmin && <TestSend email={me.email} onSent={() => qc.invalidateQueries({ queryKey: ['email-log'] })} />}</PageHeader>
      <div className="grid gap-4 xl:grid-cols-[360px_1fr]">
        <Card title="Templates">
          <ul className="divide-y text-sm">
            {templates.data?.map((t) => (
              <li key={t.key}>
                <button onClick={() => setKey(t.key)} className={`w-full px-2 py-2 text-left hover:bg-slate-50 ${t.key === key ? 'bg-blue-50' : ''}`}>
                  <span className="font-semibold">{t.label}</span>
                  {t.customer && <span className="ml-2 rounded bg-slate-200 px-1.5 text-xs">customer · manual</span>}
                  <span className="block text-xs text-slate-500">To: {t.to} · v{t.version}</span>
                </button>
              </li>
            ))}
          </ul>
        </Card>
        {current ? (
          <TemplateEditor key={`${current.key}-${current.version}`} t={current} canEdit={isAdmin} onSaved={() => qc.invalidateQueries({ queryKey: ['email-templates'] })} />
        ) : (
          <Card><p className="text-sm text-slate-500">Choose a template to see or edit it.</p></Card>
        )}
      </div>

      <Card
        title="Email log"
        className="mt-4"
        actions={
          <Select value={status} onChange={(e) => setStatus(e.target.value)} className="w-40">
            <option value="">All</option>
            <option value="QUEUED">Waiting</option>
            <option value="SENT">Sent</option>
            <option value="FAILED">Failed</option>
          </Select>
        }
      >
        <Table head={['When', 'Status', 'To', 'Subject', 'Job', 'Tries', 'Gmail ID / error']} empty={log.data?.length === 0}>
          {log.data?.map((l) => (
            <tr key={l.id}>
              <td className="px-3 py-2 whitespace-nowrap">{dateTime(l.sentAt ?? l.createdAt)}</td>
              <td className={`px-3 py-2 font-semibold ${l.status === 'FAILED' ? 'text-red-700' : l.status === 'SENT' ? 'text-green-800' : 'text-amber-800'}`}>{l.status}</td>
              <td className="px-3 py-2">{l.to}</td>
              <td className="px-3 py-2">{l.subject}</td>
              <td className="px-3 py-2">{l.workOrderId ? <Link className="text-blue-800 hover:underline" to={`/work-orders/${l.workOrderId}`}>open</Link> : '—'}</td>
              <td className="px-3 py-2">{l.attempts}</td>
              <td className="px-3 py-2 text-xs">{l.status === 'SENT' ? l.gmailMessageId : l.error}</td>
            </tr>
          ))}
        </Table>
      </Card>
    </>
  );
}

function TemplateEditor({ t, canEdit, onSaved }: { t: Template; canEdit: boolean; onSaved: () => void }) {
  const [subject, setSubject] = useState(t.subject);
  const [body, setBody] = useState(t.body);
  const [error, setError] = useState<string | null>(null);
  const [saved, setSaved] = useState(false);
  const [showVersions, setShowVersions] = useState(false);
  const versions = useQuery({ queryKey: ['email-template-versions', t.key], queryFn: () => api.get<Version[]>(`/api/email/templates/${t.key}/versions`), enabled: showVersions });
  const changed = subject !== t.subject || body !== t.body;

  const save = async () => {
    setError(null);
    try {
      await api.post(`/api/email/templates/${t.key}`, { subject, body });
      setSaved(true);
      onSaved();
    } catch (e) {
      setError(errorText(e));
    }
  };

  return (
    <Card
      title={`${t.label} (version ${t.version})`}
      actions={<Button variant="ghost" onClick={() => setShowVersions(!showVersions)}>{showVersions ? 'Hide history' : 'History'}</Button>}
    >
      <div className="space-y-3">
        <ErrorText error={error} />
        {saved && <p className="rounded-md bg-green-50 px-3 py-2 text-sm text-green-800">Saved as a new version. Earlier versions are kept.</p>}
        <p className="text-sm text-slate-600">Sent to: {t.to}{t.customer && ' — only when someone previews and presses Send on the work order.'}</p>
        <Field label="Subject"><Input value={subject} onChange={(e) => setSubject(e.target.value)} disabled={!canEdit} /></Field>
        <Field label="Message"><Textarea rows={14} value={body} onChange={(e) => setBody(e.target.value)} disabled={!canEdit} className="font-mono" /></Field>
        <p className="text-xs text-slate-500">
          You can use: {t.vars.map((v) => <code key={v} className="mr-1 rounded bg-slate-100 px-1">{`{{${v}}}`}</code>)}
        </p>
        {canEdit ? (
          <div className="flex justify-end gap-2">
            <Button variant="secondary" disabled={!changed} onClick={() => { setSubject(t.subject); setBody(t.body); }}>Undo changes</Button>
            <Button disabled={!changed} onClick={save}>Save new version</Button>
          </div>
        ) : (
          <p className="text-xs text-slate-500">Only an Admin can change templates.</p>
        )}
        {showVersions && (
          <ul className="space-y-2 border-t pt-3 text-xs">
            {versions.data?.map((v) => (
              <li key={v.id}>
                <span className="font-semibold">v{v.version}</span> · {dateTime(v.createdAt)} · {v.subject}
              </li>
            ))}
          </ul>
        )}
      </div>
    </Card>
  );
}

function TestSend({ email, onSent }: { email: string | null; onSent: () => void }) {
  const [to, setTo] = useState(email ?? '');
  const [msg, setMsg] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const send = async () => {
    setBusy(true);
    setMsg(null);
    setError(null);
    try {
      const r = await api.post<{ status: string; transport: string; gmailMessageId: string | null; error: string | null }>('/api/email/test', { to });
      if (r.status === 'SENT') setMsg(r.transport === 'gmail' ? `Sent (Gmail ID ${r.gmailMessageId}). Check the inbox.` : `Logged only (${r.transport} transport — not a real send on this server).`);
      else setError(`Not sent yet: ${r.error}`);
      onSent();
    } catch (e) {
      setError(errorText(e));
    } finally {
      setBusy(false);
    }
  };
  return (
    <div className="flex flex-wrap items-center gap-2">
      <div className="w-64"><Input value={to} onChange={(e) => setTo(e.target.value)} placeholder="you@company.com" /></div>
      <Button variant="secondary" onClick={send} disabled={busy || !to}>Send test email</Button>
      {msg && <span className="text-sm text-green-800">{msg}</span>}
      {error && <span className="text-sm text-red-700">{error}</span>}
    </div>
  );
}
